// src/Tracker.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

// Mapping display names (use Unicode escapes to avoid encoding issues)
const DISPLAY = {
  'Track 1': '\u{1F4C8} Stocks', // 📈
  'Track 2': '\u{1F6E0}\u{FE0F} Career', // 🛠️
  'Track 3': '\u{1F680} Business', // 🚀
};
const pretty = (track) => DISPLAY[track] || track;
const TRACKS = ['Track 1', 'Track 2', 'Track 3'];

// ---------- Small UI atoms ----------
const card = { background: '#111', border: '1px solid #222', borderRadius: 12, padding: 16 };
const h3 = { marginTop: 0 };
const formRow = { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' };
const inp = { background: '#1a1a1a', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' };
const btn = { background: '#12b886', color: '#000', border: 'none', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' };
const btnMini = { ...btn, padding: '6px 10px' };
const btnMiniSecondary = { ...btnMini, background: '#2b2b2b', color: '#fff' };
const btnMiniDanger = { background: '#e03131', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' };
const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #222' };
const rowOverdue = { borderLeft: '3px solid #e03131', background: '#151111' };

function Empty({ text }) {
  return <div style={{ opacity: 0.7, fontStyle: 'italic' }}>{text}</div>;
}

function TrackTabs({ activeTrack, setActiveTrack }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {TRACKS.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => setActiveTrack(t)}
          style={{
            ...btnMini,
            background: activeTrack === t ? '#ffd43b' : '#2b2b2b',
            color: activeTrack === t ? '#000' : '#fff',
          }}
        >
          {pretty(t)}
        </button>
      ))}
    </div>
  );
}

// ---------- Safe fetch that insists on JSON ----------
async function safeJsonFetch(url, options) {
  const res = await fetch(url, options);
  const text = await res.text();
  const ct = res.headers.get('content-type') || '';

  if (!res.ok) {
    // Try to parse structured error
    try {
      const j = JSON.parse(text);
      throw new Error(j.error || JSON.stringify(j));
    } catch {
      throw new Error(text.slice(0, 500));
    }
  }

  if (ct.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Invalid JSON from server');
    }
  }

  throw new Error(text.slice(0, 500));
}

// ---------- Suggestions rules (local heuristics) ----------
function buildSuggestions(track, tasks, goals, logs) {
  const textList = (arr) => arr.map((a) => (a.title || a.entry || '')).join(' || ').toLowerCase();
  const allText = textList(tasks) + ' || ' + textList(goals) + ' || ' + textList(logs);
  const has = (kw) => allText.includes(String(kw).toLowerCase());
  const suggestions = [];

  if (track === 'Track 1') {
    if (!has('broker') && !has('interactive') && !has('al ramz') && !has('dfm')) {
      suggestions.push('Decide a broker (Al Ramz, Interactive Brokers, or a DFM member) and create a task to open the account.');
    }
    if (has('interactive') && !has('deposit')) {
      suggestions.push('Create a task: "Resolve Interactive Brokers deposit issue (try bank wire or supported card)."');
    }
    if (!has('watchlist')) suggestions.push('Build a 10-stock watchlist with entry/exit criteria.');
    if (!has('risk') && !has('position size')) suggestions.push('Write a 1-page risk plan (max loss/day, position sizing).');
    if (!has('journal')) suggestions.push('Start a trade journal: log each idea, thesis, entry/exit, and outcome.');
  }

  if (track === 'Track 2') {
    if (!has('kpi') && !has('cycles') && !has('blowdown')) {
      suggestions.push('Add a task: "Review boiler cycles and blowdown calculation for this week."');
    }
    if (!has('cooling') && !has('corrosion') && !has('cti')) {
      suggestions.push('Plan cooling tower check: corrosion rate, LSI/RSI, biocide schedule.');
    }
    if (!has('cert') && !has('course')) {
      suggestions.push('Pick 1 certification/course and set a target date.');
    }
    if (logs.slice(0, 3).some((l) => String(l.outcome || '').includes('blocked'))) {
      suggestions.push("Create a task to remove yesterday's blocker with 1 experiment or data check.");
    }
  }

  if (track === 'Track 3') {
    if (!has('idea') && !has('market')) {
      suggestions.push('Brainstorm 5 ideas tied to your skills; pick 1 to validate this week.');
    }
    if (!has('landing') && !has('mvp')) {
      suggestions.push('Create a one-page landing (problem, promise, CTA) to collect interest.');
    }
    if (!has('pricing') && !has('survey')) {
      suggestions.push('Draft a 3-question survey to test willingness to pay and key pain points.');
    }
    if (!has('outreach') && !has('customer')) {
      suggestions.push('Schedule outreach to 3 potential users to discuss the problem and current workaround.');
    }
  }

  const todoCount = tasks.filter((t) => t.status !== 'done').length;
  if (todoCount > 8) suggestions.push('Too many open tasks. Pick top 3 and pause/archive the rest for clarity.');
  if (!goals.length) suggestions.push('Add 1 quarterly goal to anchor your tasks.');

  return suggestions;
}

// ---------- Main component ----------
export default function Tracker({ session }) {
  const user = session?.user;
  const userId = user?.id || null;

  const [activeTrack, setActiveTrack] = useState(TRACKS[0]);

  // Supabase data
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [logs, setLogs] = useState([]);

  // Forms
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDue, setNewTaskDue] = useState('');
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newGoalDate, setNewGoalDate] = useState('');
  const [newLogEntry, setNewLogEntry] = useState('');
  const [newLogOutcome, setNewLogOutcome] = useState('accomplished');

  // Per-task AI UI state
  const [openTaskId, setOpenTaskId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const todayStr = new Date().toISOString().slice(0, 10);

  // Fetch tasks/goals/logs
  const fetchAll = useCallback(async () => {
    if (!userId) return;

    const [{ data: t, error: et }, { data: g, error: eg }, { data: l, error: el }] = await Promise.all([
      supabase.from('tasks').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      supabase.from('goals').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      supabase.from('logs').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
    ]);

    if (!et) setTasks(t || []);
    if (!eg) setGoals(g || []);
    if (!el) setLogs(l || []);
  }, [userId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const tasksByTrack = useMemo(() => tasks.filter((x) => x.track === activeTrack), [tasks, activeTrack]);
  const goalsByTrack = useMemo(() => goals.filter((x) => x.track === activeTrack), [goals, activeTrack]);
  const logsByTrack = useMemo(() => logs.filter((x) => x.track === activeTrack), [logs, activeTrack]);

  const isOverdue = useCallback(
    (task) => !!task.due_date && task.status !== 'done' && task.due_date < todayStr,
    [todayStr]
  );

  const localSuggestions = useMemo(
    () => buildSuggestions(activeTrack, tasksByTrack, goalsByTrack, logsByTrack),
    [activeTrack, tasksByTrack, goalsByTrack, logsByTrack]
  );

  const todaysStep = useMemo(() => {
    const overdueFirst = tasksByTrack.find(isOverdue);
    if (overdueFirst) return `Finish overdue task: "${overdueFirst.title}"`;

    const dueSoon = tasksByTrack
      .filter((t) => t.due_date && t.status !== 'done')
      .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
    if (dueSoon) return `Make progress on: "${dueSoon.title}" (due ${dueSoon.due_date})`;

    const firstTodo = tasksByTrack.find((t) => t.status === 'todo');
    if (firstTodo) return `Start: "${firstTodo.title}"`;

    if (localSuggestions.length) return localSuggestions[0];
    return 'Add one small task to push this track forward today.';
  }, [tasksByTrack, localSuggestions, isOverdue]);

  // ---------- CRUD - create ----------
  const addTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !userId) return;
    const { error } = await supabase.from('tasks').insert({
      user_id: userId,
      track: activeTrack,
      title: newTaskTitle.trim(),
      due_date: newTaskDue || null,
      status: 'todo',
    });
    if (!error) {
      setNewTaskTitle('');
      setNewTaskDue('');
      fetchAll();
    }
  };

  const addGoal = async (e) => {
    e.preventDefault();
    if (!newGoalTitle.trim() || !userId) return;
    const { error } = await supabase.from('goals').insert({
      user_id: userId,
      track: activeTrack,
      title: newGoalTitle.trim(),
      target_date: newGoalDate || null,
      status: 'active',
    });
    if (!error) {
      setNewGoalTitle('');
      setNewGoalDate('');
      fetchAll();
    }
  };

  const addLog = async (e) => {
    e.preventDefault();
    if (!newLogEntry.trim() || !userId) return;
    const { error } = await supabase.from('logs').insert({
      user_id: userId,
      track: activeTrack,
      entry: newLogEntry.trim(),
      outcome: newLogOutcome,
    });
    if (!error) {
      setNewLogEntry('');
      setNewLogOutcome('accomplished');
      fetchAll();
    }
  };

  // ---------- CRUD - update/delete ----------
  const toggleTaskStatus = async (task) => {
    const cycle = { todo: 'doing', doing: 'done', done: 'todo' };
    const next = cycle[task.status] || 'todo';
    const { error } = await supabase
      .from('tasks')
      .update({ status: next })
      .eq('id', task.id)
      .eq('user_id', userId);
    if (!error) fetchAll();
  };

  const markGoal = async (goal, status) => {
    const { error } = await supabase
      .from('goals')
      .update({ status })
      .eq('id', goal.id)
      .eq('user_id', userId);
    if (!error) fetchAll();
  };

  const delTask = async (id) => {
    const { error } = await supabase.from('tasks').delete().eq('id', id).eq('user_id', userId);
    if (!error) fetchAll();
  };

  const delGoal = async (id) => {
    const { error } = await supabase.from('goals').delete().eq('id', id).eq('user_id', userId);
    if (!error) fetchAll();
  };

  const delLog = async (id) => {
    const { error } = await supabase.from('logs').delete().eq('id', id).eq('user_id', userId);
    if (!error) fetchAll();
  };

  // ---------- AI helpers ----------
  const handleTaskPromptChange = (taskId, value) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ai_prompt: value } : t)));
  };

  const handleGuideForTask = async (task) => {
    if (!userId) return;
    const taskId = task.id;
    const prompt = (task.ai_prompt || task.title || '').trim();
    if (!prompt) return;

    setAiLoading(true);
    setAiError('');

    try {
      const data = await safeJsonFetch('/api/ai-suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          track: task.track || activeTrack,
          hint: prompt,
        }),
      });

      const updates = {
        ai_prompt: prompt,
        ai_suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
        ai_notes: data.notes || null,
      };

      const { error } = await supabase.from('tasks').update(updates).eq('id', taskId).eq('user_id', userId);
      if (error) console.warn('update ai_suggest error:', error.message);

      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t)));
    } catch (e) {
      setAiError(String(e.message || e));
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateForTask = async (task) => {
    if (!userId) return;
    const taskId = task.id;
    const prompt = (task.ai_prompt || task.title || '').trim();
    if (!prompt) return;

    setAiLoading(true);
    setAiError('');

    try {
      const data = await safeJsonFetch('/api/ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          track: task.track || activeTrack,
          userText: prompt,
        }),
      });

      const steps = Array.isArray(data.steps) ? data.steps : [];
      const subtasks = Array.isArray(data.tasks) ? data.tasks : [];

      const updates = {
        ai_prompt: prompt,
        ai_steps: steps,
        ai_subtasks: subtasks,
        ai_notes: data.notes || null,
      };

      const { error } = await supabase.from('tasks').update(updates).eq('id', taskId).eq('user_id', userId);
      if (error) console.warn('update ai_generate error:', error.message);

      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t)));
    } catch (e) {
      setAiError(String(e.message || e));
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddAiTasks = async (task) => {
    if (!userId) return;
    const subtasks = Array.isArray(task.ai_subtasks) ? task.ai_subtasks : [];
    if (!subtasks.length) return;

    const inserts = subtasks.map((st, idx) => ({
      user_id: userId,
      track: task.track || activeTrack,
      title: st.title || `Subtask ${idx + 1} for ${task.title}`,
      due_date: null,
      status: 'todo',
    }));

    const { error } = await supabase.from('tasks').insert(inserts);
    if (error) {
      console.warn('Add AI tasks error:', error.message);
      return;
    }
    fetchAll();
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b0b0b', color: '#fff', fontFamily: 'sans-serif' }}>
      {/* top bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #222',
          position: 'sticky',
          top: 0,
          background: '#0b0b0b',
          zIndex: 10,
        }}
      >
        <div style={{ fontWeight: 700 }}>Milestones - Progress</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <TrackTabs activeTrack={activeTrack} setActiveTrack={setActiveTrack} />
          <div style={{ opacity: 0.8, fontSize: 14 }}>{session?.user?.email}</div>
        </div>
      </div>

      {/* sticky Today's Step */}
      <div style={{ position: 'sticky', top: 58, zIndex: 9, background: '#101010', borderBottom: '1px solid #222', padding: '10px 20px' }}>
        <strong>Today's Step:</strong> <span style={{ opacity: 0.9 }}>{todaysStep}</span>
      </div>

      {/* Next Best Actions (local) */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #222', background: '#0f0f0f' }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Next Best Actions</div>
        {localSuggestions.length ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {localSuggestions.slice(0, 4).map((s, i) => (
              <li key={i} style={{ opacity: 0.9, margin: '4px 0' }}>
                {s}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ opacity: 0.7, fontStyle: 'italic' }}>No suggestions yet - add a goal or a task to get tailored steps.</div>
        )}
      </div>

      {/* AI global messages */}
      {aiError && (
        <div style={{ padding: '8px 20px', color: '#ff6b6b', whiteSpace: 'pre-wrap', borderBottom: '1px solid #222', background: '#151010' }}>
          {aiError}
        </div>
      )}
      {aiLoading && (
        <div style={{ padding: '6px 20px', opacity: 0.8, fontSize: 12 }}>
          AI is working on the selected task...
        </div>
      )}

      {/* vertical stack */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 20, maxWidth: 900, margin: '0 auto' }}>
        {/* TASKS */}
        <section style={card}>
          <h3 style={h3}>Tasks</h3>
          <form onSubmit={addTask} style={formRow}>
            <input value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} placeholder="Add a task..." style={inp} />
            <input type="date" value={newTaskDue} onChange={(e) => setNewTaskDue(e.target.value)} style={inp} />
            <button type="submit" style={btn}>
              Add
            </button>
          </form>

          {tasksByTrack.length === 0 ? (
            <Empty text="No tasks yet." />
          ) : (
            tasksByTrack.map((task) => {
              const isOpen = openTaskId === task.id;
              const guidePrompt = task.ai_prompt || task.title || '';

              const suggestions = Array.isArray(task.ai_suggestions) ? task.ai_suggestions : [];
              const steps = Array.isArray(task.ai_steps) ? task.ai_steps : [];
              const subtasks = Array.isArray(task.ai_subtasks) ? task.ai_subtasks : [];
              const notes = task.ai_notes || '';

              return (
                <div key={task.id} style={{ marginBottom: 8 }}>
                  {/* main row */}
                  <div style={{ ...row, ...(isOverdue(task) ? rowOverdue : {}) }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{task.title}</div>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>
                        status: {task.status}
                        {task.due_date ? ` - due ${task.due_date}` : ''}
                        {isOverdue(task) ? ' - OVERDUE' : ''}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => toggleTaskStatus(task)} style={btnMini}>
                        Next
                      </button>

                      <button
                        type="button"
                        onClick={() => setOpenTaskId(isOpen ? null : task.id)}
                        style={btnMiniSecondary}
                      >
                        {isOpen ? 'Close AI' : 'AI Guide'}
                      </button>

                      <button type="button" onClick={() => delTask(task.id)} style={btnMiniDanger}>
                        Del
                      </button>
                    </div>
                  </div>

                  {/* per-task AI dropdown */}
                  {isOpen && (
                    <div style={{ marginTop: 4, marginLeft: 10, padding: 10, background: '#141414', borderRadius: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>AI guidance for this task</div>
                      <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                        Example: "Study options trading for this task" or "Break this into 5 steps".
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <input
                          value={guidePrompt}
                          onChange={(e) => handleTaskPromptChange(task.id, e.target.value)}
                          placeholder="Ask AI about this task..."
                          style={{
                            flex: '1 1 260px',
                            background: '#1a1a1a',
                            color: '#fff',
                            border: '1px solid #333',
                            borderRadius: 8,
                            padding: '8px',
                          }}
                        />

                        <button type="button" onClick={() => handleGuideForTask(task)} style={btnMini} disabled={aiLoading}>
                          Smart suggestions
                        </button>

                        <button
                          type="button"
                          onClick={() => handleGenerateForTask(task)}
                          style={{ ...btnMini, background: '#4dabf7', color: '#000' }}
                          disabled={aiLoading}
                        >
                          Day plan
                        </button>

                        {subtasks.length > 0 && (
                          <button type="button" onClick={() => handleAddAiTasks(task)} style={btnMiniSecondary}>
                            Add AI tasks
                          </button>
                        )}
                      </div>

                      {/* Smart suggestions */}
                      {suggestions.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Smart suggestions</div>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {suggestions.map((s, i) => (
                              <li key={i} style={{ opacity: 0.95, margin: '3px 0' }}>
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Step-by-step and suggested tasks */}
                      {(steps.length > 0 || subtasks.length > 0) && (
                        <div style={{ marginTop: 8 }}>
                          {subtasks.length > 0 && (
                            <>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>Suggested tasks (today)</div>
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {subtasks.map((t, i) => (
                                  <li key={i}>
                                    {t.title}
                                    {t.due ? <span style={{ opacity: 0.7 }}> - due {t.due}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}

                          {steps.length > 0 && (
                            <>
                              <div style={{ fontWeight: 600, margin: '8px 0 4px' }}>Step-by-step</div>
                              <ol style={{ margin: 0, paddingLeft: 18 }}>
                                {steps.map((st, i) => (
                                  <li key={i} style={{ margin: '3px 0' }}>
                                    {st}
                                  </li>
                                ))}
                              </ol>
                            </>
                          )}

                          {notes ? (
                            <div style={{ marginTop: 6, opacity: 0.8, fontStyle: 'italic' }}>{notes}</div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </section>

        {/* GOALS */}
        <section style={card}>
          <h3 style={h3}>Goals</h3>
          <form onSubmit={addGoal} style={formRow}>
            <input value={newGoalTitle} onChange={(e) => setNewGoalTitle(e.target.value)} placeholder="Add a goal..." style={inp} />
            <input type="date" value={newGoalDate} onChange={(e) => setNewGoalDate(e.target.value)} style={inp} />
            <button type="submit" style={btn}>
              Add
            </button>
          </form>

          {goalsByTrack.length === 0 ? (
            <Empty text="No goals yet." />
          ) : (
            goalsByTrack.map((goal) => (
              <div key={goal.id} style={row}>
                <div>
                  <div style={{ fontWeight: 600 }}>{goal.title}</div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    status: {goal.status}
                    {goal.target_date ? ` - target ${goal.target_date}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => markGoal(goal, 'active')} style={btnMini}>
                    Active
                  </button>
                  <button type="button" onClick={() => markGoal(goal, 'done')} style={btnMini}>
                    Done
                  </button>
                  <button type="button" onClick={() => markGoal(goal, 'paused')} style={btnMini}>
                    Pause
                  </button>
                  <button type="button" onClick={() => delGoal(goal.id)} style={btnMiniDanger}>
                    Del
                  </button>
                </div>
              </div>
            ))
          )}
        </section>

        {/* DAILY LOGS */}
        <section style={card}>
          <h3 style={h3}>Daily Log</h3>
          <form onSubmit={addLog} style={formRow}>
            <select value={newLogOutcome} onChange={(e) => setNewLogOutcome(e.target.value)} style={inp}>
              <option value="accomplished">accomplished</option>
              <option value="partial">partial</option>
              <option value="blocked">blocked</option>
            </select>
            <input
              value={newLogEntry}
              onChange={(e) => setNewLogEntry(e.target.value)}
              placeholder="What happened today?"
              style={{ ...inp, flex: 1 }}
            />
            <button type="submit" style={btn}>
              Add
            </button>
          </form>

          {logsByTrack.length === 0 ? (
            <Empty text="No logs yet." />
          ) : (
            logsByTrack.map((log) => (
              <div key={log.id} style={row}>
                <div>
                  <div>{log.entry}</div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    {log.outcome} - {new Date(log.created_at).toLocaleString()}
                  </div>
                </div>
                <button type="button" onClick={() => delLog(log.id)} style={btnMiniDanger}>
                  Del
                </button>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}
